import { FluentProvider } from "@fluentui/react-components";
import { AppLayout } from "AppLayout";
import { AiMetricsRoutes } from "apps/ai-metrics/AiMetricsRoutes";
import { DecoodaRoutes } from "apps/decooda/DecodaRoutes";
import { DiagnosticsRoutes } from "apps/diagnostics/DiagnosticRoutes";
import { projectIqRouter } from "apps/project-iq/ProjectIqRoutes";
import { Constants, ConstantValues } from "common/constants";
import { ToastHost } from "components/toast/ToastHost";
import { ChatPage } from "pages/chat";
import { GuidelinesPage } from "pages/guidelines";
import { HelpPage } from "pages/help";
import { UrlNotFoundPage } from "pages/urlNotFoundPage/UrlNotFoundPage";
import { WeHaveMovedPage } from "pages/weHaveMovedPage/WeHaveMovedPage";
import { ActivateWorkspace } from "pages/workspaceRedirect";
import { WorkspacesPage } from "pages/workspaces";
import { useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { aimsLightTheme } from "theme/customTheme";
import { getMovedUrl } from "utils/domainMigration";
import { UserAuthGuard } from "./components/authguard/UserAuthGuard";
import "index.css";
import { AcceptableUsePolicyPage } from "./pages/acceptableUsePolicy/AcceptableUsePolicyPage";
import { AdminPage } from "./pages/admin";
import { ErrorPage } from "./pages/error";
import { MyChatsPage } from "./pages/myChats";

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      {
        path: "/",
        element: (
          <UserAuthGuard
            component={<ChatPage />}
            anyOf={[
              Constants.MEMBERS_GROUP_NAME_VALUE,
              Constants.ADMINS_GROUP_NAME_VALUE,
              Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
            ]}
          />
        ),
      },
      {
        path: "/chats",
        handle: { pageTitle: "My Chats" },
        element: (
          <UserAuthGuard
            component={<MyChatsPage />}
            anyOf={[
              Constants.MEMBERS_GROUP_NAME_VALUE,
              Constants.ADMINS_GROUP_NAME_VALUE,
              Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
            ]}
          />
        ),
      },
      ...DecoodaRoutes,
      ...AiMetricsRoutes,
      ...DiagnosticsRoutes,
      {
        path: "/admin/:section?",
        handle: { pageTitle: "Admin" },
        element: (
          <UserAuthGuard
            component={<AdminPage />}
            anyOf={[
              Constants.ADMINS_GROUP_NAME_VALUE,
              Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
            ]}
          />
        ),
      },
      {
        path: "/workspaces",
        handle: { pageTitle: "Workspaces" },
        element: (
          <UserAuthGuard
            component={<WorkspacesPage />}
            anyOf={[
              Constants.MEMBERS_GROUP_NAME_VALUE,
              Constants.ADMINS_GROUP_NAME_VALUE,
              Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
            ]}
          />
        ),
      },
      {
        path: "/workspace/:id",
        handle: { pageTitle: "Workspaces" },
        element: (
          <UserAuthGuard
            component={<ActivateWorkspace />}
            anyOf={[
              Constants.MEMBERS_GROUP_NAME_VALUE,
              Constants.ADMINS_GROUP_NAME_VALUE,
              Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
            ]}
          />
        ),
      },
      {
        path: "/error",
        element: <ErrorPage />,
      },
      {
        path: "/guidelines",
        element: <GuidelinesPage />,
      },
      {
        path: "/help",
        element: <HelpPage />,
      },
      {
        path: "/acceptable-use-policy",
        handle: { pageTitle: "Acceptable Use Policy" },
        element: (
          <UserAuthGuard
            component={<AcceptableUsePolicyPage />}
            anyOf={[
              Constants.MEMBERS_GROUP_NAME_VALUE,
              Constants.ADMINS_GROUP_NAME_VALUE,
              Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
            ]}
          />
        ),
      },
      {
        path: "*",
        element: <UrlNotFoundPage />,
      },
    ],
  },
]);

const isProjectIq =
  sessionStorage.getItem("app") === "project-iq" ||
  window.location.hostname.includes("projectiq");
const projectIqBrowserRouter = createBrowserRouter(projectIqRouter);
const movedUrl = getMovedUrl(window.location);

export const App = (): JSX.Element => {
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.pathname.includes("/workspace/")) {
      sessionStorage.setItem(
        "workspaceId",
        url.pathname.replace("/workspace/", ConstantValues.EMPTY_STRING),
      );
    }

    const checkSessionExpiration = () => {
      const sessionTimestamp = localStorage.getItem(
        ConstantValues.SESSION_TIMESTAMP,
      );

      if (!sessionTimestamp) {
        return;
      }

      const lastActivityTime = parseInt(sessionTimestamp, 10);
      const currentTime = Math.floor(Date.now() / 1000);
      const secondsSinceLastActivity = currentTime - lastActivityTime;

      if (secondsSinceLastActivity > 60) {
        localStorage.removeItem(ConstantValues.DRAFT_MESSAGE);
        localStorage.removeItem(ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID);
        localStorage.removeItem(ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID);
        localStorage.removeItem(ConstantValues.SESSION_TIMESTAMP);
        console.log(
          `Session expired - cleared chat state (${secondsSinceLastActivity}s since last activity)`,
        );
      }
    };

    checkSessionExpiration();

    const saveSessionTimestamp = () => {
      localStorage.setItem(
        ConstantValues.SESSION_TIMESTAMP,
        Math.floor(Date.now() / 1000).toString(),
      );
    };

    window.addEventListener("beforeunload", saveSessionTimestamp);
    return () => {
      window.removeEventListener("beforeunload", saveSessionTimestamp);
    };
  }, []);

  // Rendered instead of the router so no auth guard starts a login on the old
  // domain; this covers both AIMS and Project IQ.
  if (movedUrl) {
    return (
      <FluentProvider theme={aimsLightTheme}>
        <WeHaveMovedPage newUrl={movedUrl} />
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={aimsLightTheme}>
      <ToastHost />
      <RouterProvider router={isProjectIq ? projectIqBrowserRouter : router} />
    </FluentProvider>
  );
};
